import { Button, Tooltip, message } from "antd";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import {
  IconCircleCheck,
  IconCircle,
  IconExclamationCircleFilled,
  IconClock,
  IconCircleX,
  IconRocket,
  IconArrowRight,
  IconLock,
} from "@tabler/icons-react";
import useEventSteps from "../../hooks/useEventSteps";

const EventSteps = ({ validate }: { validate: boolean }) => {
  const navigate = useNavigate();
  const event = useSelector((state: RootState) => state.event);
  const { pathname } = useLocation();
  const steps = useEventSteps();

  const getStepIcon = (step: any) => {
    if (step.disabled) {
      return <IconLock color="#d1d5db" size={16} />;
    }

    if (step.isAdminStep) {
      if (step.eventStatus === "rejected")
        return <IconCircleX color="#ef4444" size={16} />;
      if (step.eventStatus === "requested")
        return <IconClock color="#f59e0b" size={16} />;
      return <IconCircle color="#d1d5db" size={16} />;
    }

    if (step.isPublishStep) {
      return <IconRocket color="#0ea5e9" size={16} />;
    }

    if (
      event?.steps_info?.includes(step.info) ||
      event?.status === "published" ||
      event?.status === "scheduled"
    ) {
      return <IconCircleCheck color="#059669" size={16} />;
    }

    if (validate && !event?.steps_info?.includes(step.info) && step.validate) {
      return (
        <Tooltip title="This step is required">
          <IconExclamationCircleFilled color="#ef4444" size={16} />
        </Tooltip>
      );
    }

    return <IconCircle color="#d1d5db" size={16} />;
  };

  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      {steps
        .filter((step) => step.render)
        .map((step) => {
          const { name, path, isActive } = step;
          const active =
            isActive !== undefined
              ? isActive
              : pathname === path?.replace(":id", event.id!) || 
                (step.info === "ticket_config" && pathname.includes("/add-event-ticket-form/"));

          const isCompleted = event?.steps_info?.includes(step.info);

          const tooltipTitle = step.disabled
            ? "Complete previous steps first"
            : isCompleted
              ? `${name} - Completed`
              : name;

          return (
            <Tooltip key={name} title={tooltipTitle} placement="right">
              <Button
                type="text"
                onClick={() => {
                  if (step.disabled) {
                    message.warning(
                      "Please complete previous steps before proceeding"
                    );
                    return;
                  }
                  if (path && !path.startsWith("#")) {
                    navigate(path.replace(":id", event.id!));
                  }
                }}
                block
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  fontSize: 13,
                  borderRadius: "12px",
                  minHeight: "42px",
                  padding: "10px 12px",
                  fontWeight: active ? 600 : 500,
                  opacity: step.disabled ? 0.5 : 1,
                  cursor: step.disabled ? "not-allowed" : "pointer",
                  border: active
                    ? "1px solid #021526"
                    : "1px solid transparent",
                  gap: "10px",
                  transition: "all 0.2s ease",
                  borderLeft: active
                    ? "4px solid #021526"
                    : "4px solid transparent",
                }}
                onMouseEnter={(e) => {
                  if (!active && !step.disabled) {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      isCompleted ? "#ecfdf5" : "#f9fafb";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active && !step.disabled) {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      isCompleted ? "#f0faf4" : "transparent";
                  }
                }}
              >
                <span className="shrink-0 flex items-center">
                  {getStepIcon(step)}
                </span>
                <span className="flex-1 text-left truncate">{name}</span>
                {active && <IconArrowRight size={14} strokeWidth={2} />}
              </Button>
            </Tooltip>
          );
        })}
    </div>
  );
};

export default EventSteps;
